import { useDomainCRUDActions } from '@mm/state/actions/DomainCRUDActions';
import pageCRUDActions from '@mm/state/actions/PageCRUDActions';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { useWidgetCRUDActions } from '@mm/state/actions/WidgetCRUDActions';
import PageModel from '@mm/state/model/PageModel';
import { LinkTypes } from '@mm/state/model/PromoterModel';
import WidgetModel from '@mm/state/model/WidgetModel';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { activeChatbotSelector } from '@mm/state/selectors/viewSelectors';
import { widgetsByDialogueSelector } from '@mm/state/selectors/widgetSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { sortBy } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Dispatch } from 'redux';
import useRouter from 'use-react-router';
import { useSafeState } from '../../../common/hooks/use-safe-state';
import { BotBuilderRoutes } from '../../../routes/bot-builder-routes';
import { getConnectionStatus } from '../../features/configure/panels/sections/MaintenanceSettingsSection/FacebookConnectionStatus/FacebookConnectionStatus';
import { useShowInactiveBannerFlag } from './use-show-inactive-bot-banner-flag';

export const useEditPagePrimeState = (
  promoterKind: LinkTypes,
  preserveHashOnAutoNavigate?: boolean,
) => {
  const dispatch: Dispatch<StoreState> = useDispatch();
  const widgetCRUDActions = useWidgetCRUDActions();
  const promoterCRUDActions = usePromoterCRUDActions();
  const domainCRUDActions = useDomainCRUDActions();
  const faceBookPage = useSelector((state: StoreState) =>
    activeFacebookPageSelector(state),
  );

  const { history, location } = useRouter();

  const { chatbotId, dialogueId, promoterId } = useParams<{
    chatbotId: string;
    dialogueId: string;
    promoterId: string;
  }>();

  const widgets = useSelector((state: StoreState) =>
    sortBy<WidgetModel>(
      widgetsByDialogueSelector(
        Number(dialogueId),
        state.widget.entities || [],
      ),
      ['sortOrder'],
    ),
  );
  const [page, setPage] = useState<PageModel | undefined>(undefined);
  const [isFetching, setIsFetching] = useSafeState(true);
  const { botAlertVisible, setBotAlertVisible } = useShowInactiveBannerFlag();
  const promoter = useSelector(
    (state: StoreState) =>
      state.promoter.entities.find(entity => entity.id === Number(promoterId)),
    (l, r) => {
      return JSON.stringify(l) === JSON.stringify(r);
    },
  );
  const [connectionStatus] = getConnectionStatus(faceBookPage);
  const { chatWidgetChannel } = useSelector(activeChatbotSelector);

  useEffect(() => {
    const correctDialogueId = promoter?.pageId;
    if (
      promoter &&
      promoter.id &&
      correctDialogueId &&
      correctDialogueId !== Number(dialogueId)
    ) {
      const newUrlParams = {
        chatbotId: Number(chatbotId),
        dialogueId: correctDialogueId,
        promoterId: promoter.id,
      };

      const newUrl = (() => {
        switch (promoter.kind) {
          case 'link':
            return BotBuilderRoutes.shareLinkEdit.getLink(newUrlParams);
          case 'send_to_messenger':
            return BotBuilderRoutes.sendToMessengerEdit.getLink(newUrlParams);
          case 'checkbox':
            return BotBuilderRoutes.checkboxPluginEdit.getLink(newUrlParams);
          case 'customer_chat':
            return BotBuilderRoutes.customerChatEdit.getLink(newUrlParams);
          case 'commentGuardEdit':
            return BotBuilderRoutes.customerChatEdit.getLink(newUrlParams);
          case 'optin':
            return BotBuilderRoutes.leadPopupsEdit.getLink(newUrlParams);
          case 'instagram_welcomer':
            return BotBuilderRoutes.leadPopupsEdit.getLink(newUrlParams);
          default:
            return '';
        }
      })();

      if (newUrl) {
        if (preserveHashOnAutoNavigate) {
          history.replace(`${newUrl}${location.hash || ''}`);
        } else {
          history.replace(newUrl);
        }
      }
    }
  }, [promoter?.pageId, dialogueId, chatbotId]);

  const promotersToFetchDomains: LinkTypes[] = [
    'customer_chat',
    'checkbox',
    'send_to_messenger',
    'optin',
  ];

  useEffect(() => {
    const fetchStartBotState = async () => {
      if (promoterId && chatbotId && faceBookPage) {
        const requestsArray = [
          dispatch(pageCRUDActions.retrieveById(Number(dialogueId))),
          widgetCRUDActions.retrieveAll(Number(dialogueId)),
          promoterCRUDActions.retrievePromoterById(
            Number(chatbotId),
            Number(promoterId),
          ),
        ];
        if (
          promotersToFetchDomains.includes(promoterKind) &&
          (connectionStatus === 'OK' || chatWidgetChannel === 'web_chat')
        ) {
          requestsArray.push(
            domainCRUDActions.retrieveAll(Number(faceBookPage.id)),
          );
        }

        const [pageResponse] = await Promise.all(requestsArray);

        setIsFetching(false);

        if (pageResponse) {
          setPage(pageResponse);
        }
      }
    };

    fetchStartBotState();

    return () => {
      widgetCRUDActions.unsetEntities();
    };
  }, [dialogueId, promoterId]);

  return useMemo(
    () => ({
      widgets,
      isFetching,
      page,
      promoter,
      botAlertVisible,
      setBotAlertVisible,
    }),
    [
      JSON.stringify(widgets),
      isFetching,
      page,
      promoter,
      promoter?.keywordsEnabled,
      botAlertVisible,
      setBotAlertVisible,
    ],
  );
};
